import { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { CommunicationRecord, DocDataWithId } from '#types';
import { createCollectionStore, type CollectionStore } from '../../../../../store';
import { migrateToCommunicationLog } from '../parser/communicationParser';

type CommunicationLogBundleDoc = DocDataWithId & {
    docType?: 'communicationLogBundle';
    fieldId?: string;
    entries?: CommunicationRecord[];
};

type UseCommunicationLogBundlesParams = {
    /**
     * Preferred: absolute-ish Firestore doc path to the parent document.
     * Examples:
     * - forms/{formId}/formVersions/{formVersionId}
     * - entities/{entityId}/records/{entityRecordId}
     */
    parentDocPath?: string;
    formId?: string;
    formVersionId?: string;
    fieldId: string;
    enabled: boolean;
};

const inactiveStore = createCollectionStore<CommunicationLogBundleDoc>('__inactive__/communicationLogBundles');

export const useCommunicationLogBundles = ({
    parentDocPath,
    formId,
    formVersionId,
    fieldId,
    enabled,
}: UseCommunicationLogBundlesParams) => {
    const resolvedParentDocPath = parentDocPath ?? (
        formId && formVersionId
            ? `forms/${formId}/formVersions/${formVersionId}`
            : undefined
    );

    const bundlesPath = resolvedParentDocPath
        ? `${resolvedParentDocPath}/communicationLogBundles`
        : undefined;

    const isActive = enabled && Boolean(bundlesPath);

    const store = useMemo<CollectionStore<CommunicationLogBundleDoc>>(
        () => bundlesPath
            ? createCollectionStore<CommunicationLogBundleDoc>(bundlesPath, {
                queryOptions: {
                    extraConstraints: [
                        { type: 'where', field: 'fieldId', op: '==', value: fieldId },
                        { type: 'where', field: 'docType', op: '==', value: 'communicationLogBundle' },
                        { type: 'limit', n: 500 },
                    ],
                },
            })
            : inactiveStore,
        [bundlesPath, fieldId],
    );

    useEffect(() => {
        if (!isActive) return;
        store.connect();
        return () => store.disconnect();
    }, [isActive, store]);

    const snap = useSnapshot(store.state);

    const bundleData = useMemo<CommunicationLogBundleDoc[]>(() => {
        if (!isActive) return [];
        const result: CommunicationLogBundleDoc[] = [];
        for (const id of snap.docIds) {
            const entry = snap.docs.get(id);
            if (entry) result.push({ ...(entry.data as CommunicationLogBundleDoc), docId: id });
        }
        return result;
    }, [isActive, snap.docIds, snap.docs]);

    return useMemo(() => {
        const recordBundleDocIdByRecordId = new Map<string, string>();

        const rawRecords = bundleData.flatMap((bundle) => {
            const entries = Array.isArray(bundle.entries) ? bundle.entries : [];
            const bundleDocId = typeof bundle.docId === 'string' ? bundle.docId : undefined;

            if (bundleDocId) {
                entries.forEach((entry) => {
                    if (entry?.id) {
                        recordBundleDocIdByRecordId.set(entry.id, bundleDocId);
                    }
                });
            }

            return entries;
        });

        const normalizedRecords = migrateToCommunicationLog(rawRecords);

        const bundledRecords = normalizedRecords.filter((record) => !record.deleted);

        return {
            bundlesPath,
            bundledRecords,
            recordBundleDocIdByRecordId,
            loading: isActive ? snap.loading : false,
        };
    }, [bundleData, bundlesPath, isActive, snap.loading]);
};
